import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import STITCH_TYPES from '../stitchTypes';

// Create context
const ChartDataContext = createContext(null);

// Action types
const ACTION_TYPES = {
  SET_INITIAL_DATA: 'SET_INITIAL_DATA',
  UPDATE_DATA: 'UPDATE_DATA',
  UPDATE_KNITDATA: 'UPDATE_KNITDATA',
  UPDATE_DIMENSIONS: 'UPDATE_DIMENSIONS',
  UPDATE_COLORWAY: 'UPDATE_COLORWAY',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',
  SET_CURRENT_COLOR: 'SET_CURRENT_COLOR',
  SET_SELECTION: 'SET_SELECTION',
  SET_STITCH_MODE: 'SET_STITCH_MODE',
  SET_COLOR_MODE: 'SET_COLOR_MODE',
  SET_SHAPE_MODE: 'SET_SHAPE_MODE',
  SET_SHAPE_SELECT_MODE: 'SET_SHAPE_SELECT_MODE',
  SET_DRAWING_MODE: 'SET_DRAWING_MODE',
  SET_SELECTED_STITCH: 'SET_SELECTED_STITCH',
  SET_CURRENT_SHAPE: 'SET_CURRENT_SHAPE',
  SET_SELECTED_SHAPE: 'SET_SELECTED_SHAPE',
  UPDATE_SHAPE_STYLE: 'UPDATE_SHAPE_STYLE',
  ADD_SHAPE: 'ADD_SHAPE',
  UPDATE_SHAPES: 'UPDATE_SHAPES',
  UPDATE_SHAPE: 'UPDATE_SHAPE',
  REMOVE_SHAPE: 'REMOVE_SHAPE',
  SET_IS_SELECT: 'SET_IS_SELECT',
  PLACE_STITCH: 'PLACE_STITCH',
  RESET_TO_INITIAL: 'RESET_TO_INITIAL',
  SET_MOTIF_PLACEMENT: 'SET_MOTIF_PLACEMENT',
  SET_MOTIF_PLACEMENT_POSITION: 'SET_MOTIF_PLACEMENT_POSITION',
  SET_MOTIF_COLOR_SOURCE: 'SET_MOTIF_COLOR_SOURCE',
  UNDO: 'UNDO',
  REDO: 'REDO',
  SAVE_TO_HISTORY: 'SAVE_TO_HISTORY'
};

// Default values
const defaultColorway = {
  chromas: [
    { color: '#ffffff', icon: '', isSolid: false },
    { color: '#a9a9a9', icon: 'circle', isSolid: true },
    { color: '#808080', icon: 'square', isSolid: false },
    { color: '#696969', icon: 'star', isSolid: true }
  ]
};

const defaultSettings = {
  cellSize: 16,
  yscale: 1,
  thickLineEveryX: 5,
  thickLineEveryY: 5,
  showIcons: true,
  startRow: 1,
  startCol: 1,
  noStitchColor: '#e0e0e0',
  showLabels: {
    right: true,
    left: false,
    top: false,
    bottom: true
  },
  labelDirection: {
    vertical: 'down', // 'up' or 'down'
    horizontal: 'right' // 'left' or 'right'
  },
  alternatingLabels: false, // true for flat knitting (odd/even sides), false for round knitting
  margin: 24 // Space for row and column labels (in pixels)
};

// Initial state
const initialState = {
  data: [[0]],
  knitdata: [[0]],
  initialData: [[0]],
  initialKnitdata: [[0]],
  rows: 1,
  cols: 1,
  colorway: defaultColorway,
  initialColorway: defaultColorway,
  settings: defaultSettings,
  currentColor: 0,
  selection: null,
  stitchMode: false,
  colorMode: false,
  shapeMode: false,
  shapeSelectMode: false, // true when in shape selection mode, false when in shape drawing mode
  drawingMode: 'single', // 'single' or 'fill'
  selectedStitch: 'k',
  currentShape: null, // 'line', 'rectangle', 'text', or null
  selectedShape: null, // Reference to the currently selected shape object
  shapeStyle: {
    color: '#000000',
    thickness: 1,
    filled: false,
    fontSize: 12,
    fontFamily: 'Arial',
    bold: false,
    rotation: 0,
    width: 200,
    height: 24,
    // Icon properties
    iconType: 'arrow',
    size: 16
  },
  shapes: [], // Array of shape objects
  isSelect: false,
  hasChanges: false,
  motifForPlacement: null,
  motifPlacementPosition: { row: 0, col: 0 },
  motifColorSource: 'current', // 'current' uses chart colors, 'motif' uses motif colors
  // History management
  history: [],
  historyIndex: -1,
  canUndo: false,
  canRedo: false
};

// Helper functions for history management
function createHistoryEntry(state) {
  return {
    data: state.data.map(row => [...row]),
    knitdata: state.knitdata.map(row => [...row]),
    colorway: { chromas: [...state.colorway.chromas.map(chroma => ({ ...chroma }))] },
    settings: { ...state.settings },
    shapes: state.shapes.map(shape => ({ ...shape }))
  };
}

function saveToHistory(state) {
  const historyEntry = createHistoryEntry(state);
  const newHistory = state.history.slice(0, state.historyIndex + 1);
  newHistory.push(historyEntry);
  
  // Limit history size to prevent memory issues
  const maxHistorySize = 50;
  if (newHistory.length > maxHistorySize) {
    newHistory.shift();
  } else {
    return {
      ...state,
      history: newHistory,
      historyIndex: newHistory.length - 1,
      canUndo: newHistory.length > 0,
      canRedo: false
    };
  }
  
  return {
    ...state,
    history: newHistory,
    historyIndex: newHistory.length - 1,
    canUndo: newHistory.length > 0,
    canRedo: false
  };
}

function restoreFromHistory(state, entry) {
  return {
    ...state,
    data: entry.data.map(row => [...row]),
    knitdata: entry.knitdata.map(row => [...row]),
    colorway: { chromas: [...entry.colorway.chromas.map(chroma => ({ ...chroma }))] },
    settings: { ...entry.settings },
    shapes: entry.shapes ? entry.shapes.map(shape => ({ ...shape })) : [],
    rows: entry.data.length,
    cols: entry.data[0] ? entry.data[0].length : 0,
    hasChanges: true
  };
}

// Reducer function
function chartDataReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_INITIAL_DATA: {
      const { data, knitdata, colorway, settings, shapes, rows, cols } = action.payload;
      const initialState = {
        ...state,
        data: [...data.map(row => [...row])],
        knitdata: [...knitdata.map(row => [...row])],
        initialData: [...data.map(row => [...row])],
        initialKnitdata: [...knitdata.map(row => [...row])],
        colorway: { chromas: [...colorway.chromas.map(chroma => ({ ...chroma }))] },
        initialColorway: { chromas: [...colorway.chromas.map(chroma => ({ ...chroma }))] },
        settings: { ...defaultSettings, ...settings },
        shapes: shapes ? [...shapes.map(shape => ({ ...shape }))] : [],
        rows,
        cols,
        hasChanges: false
      };
      
      // Initialize history with the initial state
      const historyEntry = createHistoryEntry(initialState);
      return {
        ...initialState,
        history: [historyEntry],
        historyIndex: 0,
        canUndo: false,
        canRedo: false
      };
    }
    
    case ACTION_TYPES.UPDATE_DATA: {
      const newData = action.payload;
      const newState = {
        ...state,
        data: newData,
        rows: newData ? newData.length : 0,
        cols: newData && newData.length > 0 ? newData[0].length : 0,
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.UPDATE_KNITDATA: {
      const newState = {
        ...state,
        knitdata: action.payload,
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.UPDATE_DIMENSIONS: {
      const { rows, cols } = action.payload;
      return {
        ...state,
        rows,
        cols
      };
    }
    
    case ACTION_TYPES.UPDATE_COLORWAY: {
      const newState = {
        ...state,
        colorway: action.payload,
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.UPDATE_SETTINGS: {
      return {
        ...state,
        settings: { ...state.settings, ...action.payload },
        hasChanges: true
      };
    }
    
    case ACTION_TYPES.SET_CURRENT_COLOR: {
      return {
        ...state,
        currentColor: action.payload
      };
    }
    
    case ACTION_TYPES.SET_SELECTION: {
      return {
        ...state,
        selection: action.payload
      };
    }
    
    case ACTION_TYPES.SET_STITCH_MODE: {
      return {
        ...state,
        stitchMode: action.payload
      };
    }
    
    case ACTION_TYPES.SET_COLOR_MODE: {
      return {
        ...state,
        colorMode: action.payload
      };
    }
    
    case ACTION_TYPES.SET_SHAPE_MODE: {
      return {
        ...state,
        shapeMode: action.payload
      };
    }
    
    case ACTION_TYPES.SET_SHAPE_SELECT_MODE: {
      return {
        ...state,
        shapeSelectMode: action.payload
      };
    }
    
    case ACTION_TYPES.SET_DRAWING_MODE: {
      return {
        ...state,
        drawingMode: action.payload
      };
    }
    
    case ACTION_TYPES.SET_SELECTED_STITCH: {
      return {
        ...state,
        selectedStitch: action.payload
      };
    }
    
    case ACTION_TYPES.SET_CURRENT_SHAPE: {
      return {
        ...state,
        currentShape: action.payload
      };
    }
    
    case ACTION_TYPES.SET_SELECTED_SHAPE: {
      return {
        ...state,
        selectedShape: action.payload
      };
    }
    
    case ACTION_TYPES.UPDATE_SHAPE_STYLE: {
      return {
        ...state,
        shapeStyle: action.payload
      };
    }
    
    case ACTION_TYPES.ADD_SHAPE: {
      const newState = {
        ...state,
        shapes: [...state.shapes, action.payload],
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.UPDATE_SHAPES: {
      const newState = {
        ...state,
        shapes: action.payload,
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.UPDATE_SHAPE: {
      const newState = {
        ...state,
        shapes: state.shapes.map(shape => 
          shape.id === action.payload.id ? { ...shape, ...action.payload.updates } : shape
        ),
        selectedShape: action.payload.id === state.selectedShape?.id 
          ? { ...state.selectedShape, ...action.payload.updates } 
          : state.selectedShape,
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.REMOVE_SHAPE: {
      const newState = {
        ...state,
        shapes: state.shapes.filter(shape => shape.id !== action.payload),
        selectedShape: state.selectedShape?.id === action.payload ? null : state.selectedShape,
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.SET_IS_SELECT: {
      return {
        ...state,
        isSelect: action.payload
      };
    }
    
    case ACTION_TYPES.PLACE_STITCH: {
      const { row, col } = action.payload;
      
      if (!state.stitchMode || !state.selectedStitch) return state;

      const newKnitdata = [...state.knitdata.map(r => [...r])];
      
      // Ensure the knitdata array is big enough
      while (newKnitdata.length <= row) {
        newKnitdata.push(Array.from({ length: state.cols }, () => ''));
      }
      while (newKnitdata[row].length <= col) {
        newKnitdata[row].push('');
      }

      const stitchInfo = STITCH_TYPES[state.selectedStitch];
      const stitchWidth = stitchInfo?.width || 1;
      
      // Simple logic: clicked column is always the start column in data space
      const startCol = col;
      const endCol = startCol + stitchWidth - 1;
      
      // Check if there's enough space
      if (startCol < 0 || endCol >= state.cols) return state;
      
      // Clear any existing overlapping stitches
      for (let i = startCol; i <= endCol; i++) {
        const existingStitch = newKnitdata[row][i];
        if (existingStitch === null) {
          // Find and clear the multi-cell stitch
          let start = i;
          while (start > 0 && newKnitdata[row][start] === null) start--;
          const multiStitch = newKnitdata[row][start];
          const multiStitchWidth = STITCH_TYPES[multiStitch]?.width || 1;
          for (let j = 0; j < multiStitchWidth; j++) {
            if (start + j < state.cols) {
              newKnitdata[row][start + j] = '';
            }
          }
        } else if (existingStitch && existingStitch !== '') {
          newKnitdata[row][i] = '';
        }
      }
      
      // Place the new stitch
      newKnitdata[row][startCol] = state.selectedStitch;
      
      // Mark subsequent cells as null for multi-cell stitches
      for (let i = 1; i < stitchWidth; i++) {
        const cellIndex = startCol + i;
        if (cellIndex < state.cols) {
          newKnitdata[row][cellIndex] = null;
        }
      }
      
      const newState = {
        ...state,
        knitdata: newKnitdata,
        hasChanges: true
      };
      return saveToHistory(newState);
    }
    
    case ACTION_TYPES.RESET_TO_INITIAL: {
      return {
        ...state,
        data: [...state.initialData.map(row => [...row])],
        knitdata: [...state.initialKnitdata.map(row => [...row])],
        hasChanges: false
      };
    }

    case ACTION_TYPES.SET_MOTIF_PLACEMENT: {
      return {
        ...state,
        motifForPlacement: action.payload,
        motifPlacementPosition: { row: 0, col: 0 }, // Reset position when setting new motif
        motifColorSource: 'current' // Reset to current chart colors by default
      };
    }

    case ACTION_TYPES.SET_MOTIF_PLACEMENT_POSITION: {
      return {
        ...state,
        motifPlacementPosition: action.payload
      };
    }

    case ACTION_TYPES.SET_MOTIF_COLOR_SOURCE: {
      return {
        ...state,
        motifColorSource: action.payload
      };
    }

    case ACTION_TYPES.SAVE_TO_HISTORY: {
      return saveToHistory(state);
    }

    case ACTION_TYPES.UNDO: {
      if (state.historyIndex <= 0) return state;
      
      const prevIndex = state.historyIndex - 1;
      const historyEntry = state.history[prevIndex];
      
      return {
        ...restoreFromHistory(state, historyEntry),
        historyIndex: prevIndex,
        canUndo: prevIndex > 0,
        canRedo: true
      };
    }

    case ACTION_TYPES.REDO: {
      if (state.historyIndex >= state.history.length - 1) return state;
      
      const nextIndex = state.historyIndex + 1;
      const historyEntry = state.history[nextIndex];
      
      return {
        ...restoreFromHistory(state, historyEntry),
        historyIndex: nextIndex,
        canUndo: true,
        canRedo: nextIndex < state.history.length - 1
      };
    }
    
    default:
      return state;
  }
}

// Provider component
export function ChartDataProvider({ children, motif, item, onSaveProperty }) {
  const [state, dispatch] = useReducer(chartDataReducer, initialState);
  
  // Initialize data when motif changes
  useEffect(() => {
    if (motif) {
      let _data = motif.data || motif.colordata || [[]];
      let _knitdata = motif.knitdata || [[]];
      let _rows = _data.length || _knitdata.length;
      let _cols = _data[0] ? _data[0].length : _knitdata[0] ? _knitdata[0].length : 0;
      let _colors = motif.colors || defaultColorway.chromas.map(chroma => chroma.color);
      let _colorway = motif.colorway || {
        chromas: _colors.map((color, index) => ({ 
          color, 
          icon: defaultColorway.chromas[index % defaultColorway.chromas.length]?.icon || '', 
          isSolid: false 
        }))
      };
      let _settings = motif.settings || {};
      let _shapes = motif.shapes || [];
      
      // Ensure data array matches dimensions
      let mutableData = _data.map(row => [...row]);
      
      if (mutableData.length < _rows) {
        for (let i = mutableData.length; i < _rows; i++) {
          mutableData.push(Array.from({ length: _cols }, () => 0));
        }
      }
      if (mutableData[0] && mutableData[0].length < _cols) {
        for (let i = 0; i < mutableData.length; i++) {
          while (mutableData[i].length < _cols) {
            mutableData[i].push(0);
          }
        }
      }
      
      dispatch({
        type: ACTION_TYPES.SET_INITIAL_DATA,
        payload: {
          data: mutableData,
          knitdata: _knitdata,
          colorway: _colorway,
          settings: _settings,
          shapes: _shapes,
          rows: _rows,
          cols: _cols
        }
      });
    }
  }, [motif]);
  
  // Action creators
  const actions = {
    updateData: useCallback((newData) => {
      dispatch({ type: ACTION_TYPES.UPDATE_DATA, payload: newData });
    }, []),
    
    updateKnitdata: useCallback((newKnitdata) => {
      dispatch({ type: ACTION_TYPES.UPDATE_KNITDATA, payload: newKnitdata });
    }, []),
    
    updateDimensions: useCallback((rows, cols) => {
      dispatch({ type: ACTION_TYPES.UPDATE_DIMENSIONS, payload: { rows, cols } });
    }, []),
    
    updateColorway: useCallback((newColorway) => {
      dispatch({ type: ACTION_TYPES.UPDATE_COLORWAY, payload: newColorway });
    }, []),
    
    updateSettings: useCallback((newSettings) => {
      dispatch({ type: ACTION_TYPES.UPDATE_SETTINGS, payload: newSettings });
    }, []),
    
    setCurrentColor: useCallback((colorIndex) => {
      dispatch({ type: ACTION_TYPES.SET_CURRENT_COLOR, payload: colorIndex });
    }, []),
    
    setSelection: useCallback((selection) => {
      dispatch({ type: ACTION_TYPES.SET_SELECTION, payload: selection });
    }, []),
    
    setStitchMode: useCallback((mode) => {
      dispatch({ type: ACTION_TYPES.SET_STITCH_MODE, payload: mode });
    }, []),
    
    setColorMode: useCallback((mode) => {
      dispatch({ type: ACTION_TYPES.SET_COLOR_MODE, payload: mode });
    }, []),
    
    setShapeMode: useCallback((mode) => {
      dispatch({ type: ACTION_TYPES.SET_SHAPE_MODE, payload: mode });
    }, []),
    
    setShapeSelectMode: useCallback((mode) => {
      dispatch({ type: ACTION_TYPES.SET_SHAPE_SELECT_MODE, payload: mode });
    }, []),
    
    setDrawingMode: useCallback((mode) => {
      dispatch({ type: ACTION_TYPES.SET_DRAWING_MODE, payload: mode });
    }, []),
    
    setSelectedStitch: useCallback((stitch) => {
      dispatch({ type: ACTION_TYPES.SET_SELECTED_STITCH, payload: stitch });
    }, []),
    
    setCurrentShape: useCallback((shape) => {
      dispatch({ type: ACTION_TYPES.SET_CURRENT_SHAPE, payload: shape });
    }, []),
    
    setSelectedShape: useCallback((shape) => {
      dispatch({ type: ACTION_TYPES.SET_SELECTED_SHAPE, payload: shape });
    }, []),
    
    updateShapeStyle: useCallback((style) => {
      dispatch({ type: ACTION_TYPES.UPDATE_SHAPE_STYLE, payload: style });
    }, []),
    
    addShape: useCallback((shape) => {
      dispatch({ type: ACTION_TYPES.ADD_SHAPE, payload: shape });
    }, []),
    
    updateShapes: useCallback((shapes) => {
      dispatch({ type: ACTION_TYPES.UPDATE_SHAPES, payload: shapes });
    }, []),
    
    updateShape: useCallback((id, updates) => {
      dispatch({ type: ACTION_TYPES.UPDATE_SHAPE, payload: { id, updates } });
    }, []),
    
    removeShape: useCallback((id) => {
      dispatch({ type: ACTION_TYPES.REMOVE_SHAPE, payload: id });
    }, []),
    
    setIsSelect: useCallback((isSelect) => {
      dispatch({ type: ACTION_TYPES.SET_IS_SELECT, payload: isSelect });
    }, []),
    
    resetData: useCallback(() => {
      dispatch({ type: ACTION_TYPES.RESET_TO_INITIAL });
    }, []),
    
    placeStitch: useCallback((row, col) => {
      // We need to use the current state, so we'll use a functional update
      dispatch({ type: ACTION_TYPES.PLACE_STITCH, payload: { row, col } });
    }, []),

    setMotifForPlacement: useCallback((motif) => {
      dispatch({ type: ACTION_TYPES.SET_MOTIF_PLACEMENT, payload: motif });
    }, []),

    setMotifPlacementPosition: useCallback((position) => {
      dispatch({ type: ACTION_TYPES.SET_MOTIF_PLACEMENT_POSITION, payload: position });
    }, []),

    setMotifColorSource: useCallback((source) => {
      dispatch({ type: ACTION_TYPES.SET_MOTIF_COLOR_SOURCE, payload: source });
    }, []),

    // Undo/Redo actions
    undo: useCallback(() => {
      dispatch({ type: ACTION_TYPES.UNDO });
    }, []),

    redo: useCallback(() => {
      dispatch({ type: ACTION_TYPES.REDO });
    }, []),

    importMotif: useCallback((motif, placement) => {
      if (motif.data || motif.colordata) {
        const motifData = motif.data || motif.colordata;
        const motifRows = motifData.length;
        const motifCols = motifData[0] ? motifData[0].length : 0;
        
        // Calculate required dimensions
        const requiredRows = Math.max(state.rows, placement.row + motifRows);
        const requiredCols = Math.max(state.cols, placement.col + motifCols);
        
        // Start with current chart data
        let newData = [...state.data.map(row => [...row])];
        
        // First extend columns for existing rows
        if (requiredCols > state.cols) {
          for (let i = 0; i < newData.length; i++) {
            while (newData[i].length < requiredCols) {
              newData[i].push(0);
            }
          }
        }
        
        // Then extend rows if needed (new rows will have the correct column count)
        if (requiredRows > state.rows) {
          for (let i = state.rows; i < requiredRows; i++) {
            newData.push(Array.from({ length: requiredCols }, () => 0));
          }
        }
        
        // Import motif data at the specified placement
        for (let i = 0; i < motifRows; i++) {
          for (let j = 0; j < motifCols; j++) {
            const targetRow = placement.row + i;
            const targetCol = placement.col + j;
            if (newData[targetRow] && newData[targetRow][targetCol] !== undefined) {
              newData[targetRow][targetCol] = motifData[i][j];
            }
          }
        }
        
        // Update the chart data
        dispatch({ type: ACTION_TYPES.UPDATE_DATA, payload: newData });
        
        // Import colors based on the selected color source
        if (state.motifColorSource === 'motif' && motif.colors && motif.colors.length > 0) {
          const newColorway = {
            chromas: motif.colors.map(color => ({ color, icon: '', isSolid: false }))
          };
          dispatch({ type: ACTION_TYPES.UPDATE_COLORWAY, payload: newColorway });
        } else if (state.motifColorSource === 'current' && motif.colors && motif.colors.length > state.colorway.chromas.length) {
          // Only extend colors if motif has more colors than current chart
          const newColorway = {
            chromas: motif.colors.map(color => ({ color, icon: '', isSolid: false }))
          };
          dispatch({ type: ACTION_TYPES.UPDATE_COLORWAY, payload: newColorway });
        }
        
        console.log(`Motif "${motif.name}" imported at position (${placement.row}, ${placement.col}), chart extended to ${requiredRows}x${requiredCols}`);
      }
      
      // Clear the placement state
      dispatch({ type: ACTION_TYPES.SET_MOTIF_PLACEMENT, payload: null });
    }, [state.data, state.rows, state.cols, state.colorway.chromas.length, state.motifColorSource]),
    
    saveData: useCallback(() => {
      if (onSaveProperty) {
        onSaveProperty('data', state.data);
        onSaveProperty('knitdata', state.knitdata);
        onSaveProperty('colorway', state.colorway);
        onSaveProperty('settings', state.settings);
        onSaveProperty('shapes', state.shapes);
        // Reset the hasChanges flag by updating initial data
        dispatch({
          type: ACTION_TYPES.SET_INITIAL_DATA,
          payload: {
            data: state.data,
            knitdata: state.knitdata,
            colorway: state.colorway,
            settings: state.settings,
            shapes: state.shapes,
            rows: state.rows,
            cols: state.cols
          }
        });
      }
    }, [onSaveProperty, state.data, state.knitdata, state.colorway, state.settings, state.shapes, state.rows, state.cols])
  };
  
  const contextValue = {
    ...state,
    // Backward compatibility: expose colors as an array
    colors: state.colorway.chromas.map(chroma => chroma.color),
    item,
    actions
  };
  
  return (
    <ChartDataContext.Provider value={contextValue}>
      {children}
    </ChartDataContext.Provider>
  );
}

// Custom hook to use the chart data context
export function useChartData() {
  const context = useContext(ChartDataContext);
  if (!context) {
    throw new Error('useChartData must be used within a ChartDataProvider');
  }
  return context;
}

export default ChartDataProvider;
